import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';
import { NotificationService as UINotificationService } from '../services/errorService';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Error from '@mui/icons-material/Error';
import Email from '@mui/icons-material/Email';

const EmailVerification: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState<string>('');
  const [countdown, setCountdown] = useState(5);
  const [showResendDialog, setShowResendDialog] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  const handleRedirect = () => {
    // Clear any email verification notifications
    const notifications = UINotificationService.getAll();
    notifications.forEach(notification => {
      if (notification.type === 'info' && notification.title === 'Verification Email Sent') {
        UINotificationService.remove(notification.id);
      }
    });

    navigate('/login?verified=true');
  };

  const handleResendEmail = async () => {
    if (!resendEmail) return;

    setResendLoading(true);
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: resendEmail,
      });

      if (error) throw error;

      UINotificationService.success('Email Sent', 'Verification email has been resent.');
      setShowResendDialog(false);
    } catch (error: any) {
      console.error('Failed to resend email:', error);
      UINotificationService.error('Error', error.message || 'Failed to resend email');
    } finally {
      setResendLoading(false);
    }
  };

  useEffect(() => {
    const checkVerification = async () => {
      // 1. Check for explicit errors in URL
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      
      if (error) {
        setError(errorDescription || 'Verification failed. Please try again.');
        setVerificationStatus('error');
        return;
      }

      // 2. Check current session status
      // If Supabase already handled the redirect (detectSessionInUrl: true), 
      // the URL params might be gone but the session should be active.
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email_confirmed_at) {
        console.log('User already verified via session');
        handleSuccess();
        return;
      }

      // 3. Check for tokens in URL (Query Params or Hash)
      const accessToken = searchParams.get('access_token');
      const token = searchParams.get('token');
      const type = searchParams.get('type');
      const code = searchParams.get('code');
      
      // Parse hash if needed (Supabase implicit flow)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const hashAccessToken = hashParams.get('access_token');
      const hashType = hashParams.get('type');

      const hasToken = accessToken || token || code || hashAccessToken;
      const validType = type === 'signup' || type === 'email' || hashType === 'signup' || hashType === 'email';

      // If we have a code, Supabase client should be exchanging it.
      // We can wait a bit for the onAuthStateChange to fire.
      if (code) {
        console.log('Authorization code detected, waiting for exchange...');
        // The auth listener below will handle the success case
        return; 
      }

      if (hasToken) {
        // If we have tokens but no session yet, it might be implicit flow handling
        console.log('Verification tokens detected');
        // We'll let the auth listener handle the state change
        return;
      }

      // 4. If no session and no tokens, and we aren't waiting for anything...
      // But we should be careful not to fail too fast if Supabase is still loading.
      // We'll rely on a timeout or the fact that we started in 'loading' state.
      
      // If we are truly stuck in loading with no tokens and no session:
      if (!hasToken && !session) {
         // Wait a short moment to ensure Supabase client has finished initialization
         setTimeout(async () => {
            const { data: { session: currentSession } } = await supabase.auth.getSession();
            if (currentSession?.user?.email_confirmed_at) {
               handleSuccess();
            } else {
               console.log('Email verification failed - no valid tokens or session found');
               setError('Invalid or expired verification link.');
               setVerificationStatus('error');
            }
         }, 2000);
      }
    };

    checkVerification();

    // Listen for auth state changes (e.g. successful code exchange)
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state change:', event);
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (session?.user?.email_confirmed_at) {
           handleSuccess();
        }
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [searchParams, navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (verificationStatus === 'success') {
      if (countdown > 0) {
        timer = setInterval(() => {
          setCountdown((prev) => prev - 1);
        }, 1000);
      } else {
        handleRedirect();
      }
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [verificationStatus, countdown]);

  const handleSuccess = () => {
    setVerificationStatus('success');
    
    // Clear notifications
    const notifications = UINotificationService.getAll();
    notifications.forEach(notification => {
      if (notification.type === 'info' && notification.title === 'Verification Email Sent') {
        UINotificationService.remove(notification.id);
      }
    });

    // Show success notification
    UINotificationService.success(
      'Email Verified!',
      'Your email has been successfully verified. You can now access all features of your account.'
    );
  };

  if (verificationStatus === 'loading') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            Verifying Your Email
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Please wait while we verify your email address...
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (verificationStatus === 'success') {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom color="success.main">
            Email Confirmed!
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Your email has been successfully verified. You will be redirected to the login page in {countdown} seconds.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRedirect}
            sx={{ mr: 2 }}
          >
            Go to Login Now
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/dashboard')}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Error sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom color="error.main">
          Verification Failed
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Typography variant="body1" sx={{ mb: 3 }}>
          The verification link appears to be invalid or has expired. You can request a new verification email.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowResendDialog(true)}
            startIcon={<Email />}
          >
            Resend Verification Email
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/login')}
          >
            Back to Login
          </Button>
        </Box>
      </Paper>

      {/* Resend Email Dialog */}
      <Dialog open={showResendDialog} onClose={() => setShowResendDialog(false)}>
        <DialogTitle>Resend Verification Email</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={resendEmail}
            onChange={(e) => setResendEmail(e.target.value)}
            margin="normal"
            placeholder="Enter your email address"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowResendDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleResendEmail}
            disabled={!resendEmail || resendLoading}
            variant="contained"
          >
            {resendLoading ? <CircularProgress size={20} /> : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmailVerification;
